import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store/store";
import axios from "axios";
import { getUserPlaybooks } from "../services/playbook-service";
import { setPlaybookMetadata } from "../store/playbook-analysis-slice";
import { response } from "./response";
import { diffWords } from "diff";

export const usePlaybook = () => {
	const userData = useSelector((state: RootState) => state.user.userData);
	const fileData = useSelector((state: RootState) => state.files);
	const dispatch = useDispatch();
	const apiUrl = process.env.API_URL;

	function cleanArray(arr) {
		let start = 0;
		let end = arr.length - 1;

		// Remove leading false
		while (start < arr.length && arr[start].added === false && arr[start].removed === false) {
			start++;
		}

		// Remove trailing false
		while (end >= 0 && arr[end].added === false && arr[end].removed === false) {
			end--;
		}

		return arr.slice(start, end + 1);
	}

	async function switchPlaybook(playbookId: any, userId: string, contractType) {
		try {
			const payload = {
				userId,
				playbookId,
				contractType,
			};

			// Making the request to the backend
			const res = await axios.post(apiUrl + "/api/v1/playbook/toggle", payload, {
				headers: {
					Authorization: `Bearer ${localStorage.getItem("authToken")}`,
				},
			});

			if (res.data.success) {
				return response.success(res.data);
			} else {
				return response.error(res.data);
			}
		} catch (err) {
			return response.error({ frontend_error: err.message });
		}
	}

	const autoActivatePlaybook = async (contractType: string, userId) => {
		const payload = { userId, contractType };
		try {
			const response = await axios.post(`${apiUrl}/api/v1/playbook/get-active-playbook`, payload, {
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${localStorage.getItem("authToken")}`,
				},
			});
			dispatch(setPlaybookMetadata({ id: response.data.active_playbook }));
			return response;
		} catch (error) {
			console.error("Error making API call:", error);
		}
	};

	/**
	 * Function to take in all the text content from the document > convert it to a PDF in memory > send it to our backend.
	 * @param textData: any, this is the text content from the current open document.
	 * @param hasImages: Boolean, this is an identifier if the current contract has images or not.
	 */

	async function analyzePlaybook() {
		try {
			// Getting active playbook for the user
			const userPlaybooks = await getUserPlaybooks(userData.userId);
			// console.log(userPlaybooks);
			const activePlaybook = userPlaybooks.filter((elem) => elem.active);

			const payload = {
				userId: userData.userId,
				playbookId: activePlaybook[0].playbookId,
				fileId: fileData.fileId,
			};

			// Making the playbook analysis API request
			const res = await axios.post(apiUrl + "/api/v1/playbook/analyze-playbook", payload, {
				timeout: 500000000,
				headers: {
					Authorization: `Bearer ${localStorage.getItem("authToken")}`,
				},
			});

			if (res.data.success === true) {
				const responseData = res.data.data.response;

				// using array for change element instead of hashmap, becuase it had more manipulations to render it than
				// its advantages of instant lookup

				const createInnerStructure = () => ({ totalRisks: 0, data: [] });

				// using function because if i create innerStructure as a normal object
				// and pass it in all these 3 then it will cause pass by reference
				const cleanedData = {
					contradictory: createInnerStructure(),
					missing: createInnerStructure(),
					change: createInnerStructure(),
					favourable: createInnerStructure(),
				};

				responseData.forEach((item) => {
					// console.log(item);
					const commonData = {
						key: item.key,
						standardAnswer: item.standard_answer,
						mitigation: [],
					};

					// const changesElements = [];
					const tempElements = { contradictory: [], favourable: [], missing: [], change: [] };

					item.analysis?.forEach((elem) => {
						if (elem.accepted != null) {
							cleanedData.change.totalRisks += 1;
							tempElements.change.push(elem);
							elem.diff = diffWords(elem.reference ?? "", elem.new_version ?? item.standard_answer);
						} else {
							cleanedData[elem.comparison_type].totalRisks += 1;
							if (elem.reference && elem.new_version) {
								// diffing right here because it would be costly to diff later during rendering
								// as i would need to diff everytime someone opens up a risk
								elem.diff = diffWords(elem.reference, elem.new_version);
								// console.log(elem.diff);
							} else {
								// for missing terms
								elem.diff = diffWords("", item.standard_answer);
							}
							tempElements[elem.comparison_type].push(elem);
						}
					});

					Object.keys(cleanedData).forEach((elem2) => {
						const tempItems = { ...commonData };
						if (tempElements[elem2].length > 0) {
							cleanedData[elem2].data.push({
								...tempItems,
								mitigation: tempElements[elem2],
							});
						}
					});
				});

				return response.success({ data: cleanedData });
			} else {
				return response.error(res.data);
			}
		} catch (error) {
			console.log(error);
			return response.error({ frontend_error: error.message });
		}
	}

	/**
	 * This function generates a playbook using the current open document.
	 * @param userId: Unique ID of the user.
	 * @param fileId: Unique ID for the file which is returned by the upload function.
	 */
	type PlaybookOptions = {
		fileId?: string;
		file?: any;
		contractType?: any;
		userId: string;
	};

	async function generatePlaybook({ contractType = null, file = null, userId }: PlaybookOptions) {
		try {
			const formData = new FormData();

			// Skip null values in FormData to prevent them from being sent as the string "null",
			// which the backend can't handle properly.

			/*
			 *Here i added playbook_tpye in else because we only playbook type if we cant detect it
			 * and currently we cant detect it if its in csv format so we need it only in that condition
			 */
			if (file.fileId) {
				formData.append("file_id", file.fileId);
			} else {
				formData.append("playbook_type", contractType);
			}

			if (file) {
				formData.append("csv", file);
			}
			formData.append("user_id", userId);
			/**
			 * This conditional here means that if we have uploaded a pdf or a doc it means
			 * that the payload is as it is coming from the response of upload contract api
			 * and in that api we call contract as contractName bubt if user uploaded a csv then we are using its blob
			 * and in that blob it is called "name" i cant rename ContractName to name because in that same payload of pdf
			 * it is already using "name" key for contract type
			 */
			formData.append("playbook_name", file.fileId ? file.ContractName : file?.name);

			// Making the playbook generation request to the backend
			const res = await axios.post(`${apiUrl}/api/v1/playbook/generate-playbook`, formData, {
				headers: {
					"Content-Type": "multipart/form-data",
					Authorization: `Bearer ${localStorage.getItem("authToken")}`,
				},
			});
			if (res.data.success) {
				return response.success(res.data);
			} else {
				return response.error(res.data);
			}
		} catch (error) {
			return response.error({ frontend_error: error.message });
		}
	}

	async function fetchPlaybookTerms(playbook_id, userId) {
		try {
			const res = await axios.get(apiUrl + "/api/v1/add-in/get-user-playbooks", {
				params: {
					userId,
					playbookId: playbook_id,
				},
				headers: {
					Authorization: `Bearer ${localStorage.getItem("authToken")}`,
				},
			});
			if (res.data.success) {
				const userPlaybooks = res.data.playbookKeys;
				return response.success({ data: userPlaybooks });
			} else {
				return response.error(res.data);
			}
		} catch (err) {
			return response.error({ frontend_error: err.message });
		}
	}

	async function trackChanges(action, id) {
		try {
			const payload = {
				action: action,
				risk_id: id,
			};
			const res = await axios.post(apiUrl + "/api/v1/playbook/track-changes", payload, {
				headers: {
					Authorization: `Bearer ${localStorage.getItem("authToken")}`,
				},
			});
			if (res.data.success) {
				return response.success(res.data);
			} else {
				return response.error(res.data);
			}
		} catch (err) {
			console.log(err);
			return response.error({ frontend_error: err.message || "An unexpected error occurred in track changes" });
		}
	}

	return { autoActivatePlaybook, analyzePlaybook, switchPlaybook, generatePlaybook, fetchPlaybookTerms, trackChanges };
};
